import { z } from 'zod';

/**
 * AE-authored custom slides (Phase 2, slice 10).
 *
 * A custom slide is a header over a rich-text body — the shape three hardcoded
 * narrative sections used to have, made data instead of code so an AE can add,
 * title, reorder and carry forward their own slides without a deploy.
 *
 * Stored as one ordered jsonb array on `sprint_packets.custom_sections`, not a
 * child table: unlike strategy groups (per-item state written independently by
 * the AI pass and the AE), a custom slide has exactly one actor and every save
 * rewrites the whole slide, so a JSON document costs nothing a child table
 * would buy back.
 */

/**
 * The built-in sections a custom slide can be placed after. Deliberately an
 * explicit literal union rather than a subtraction from `NarrativeSectionKey` —
 * that enum still carries `creative-strategy` and the four `budget-*` keys,
 * which are narrative blocks but were never sections.
 */
export const builtInSectionKeySchema = z.enum([
  'performance-review',
  'audience-framework',
  'brand-standards',
  'creative',
  'client-survey',
  'upcoming-meetings',
]);

export type BuiltInSectionKey = z.infer<typeof builtInSectionKeySchema>;

/**
 * `custom:` + the slide's `randomUUID()`. Shape-validated, not merely prefixed:
 * the key is persisted (see `hiddenSections`), so `custom:` alone or an
 * arbitrary-length string would sit in the column forever.
 */
const CUSTOM_SECTION_KEY = /^custom:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const customSectionKeySchema = z.custom<`custom:${string}`>(
  (v) => typeof v === 'string' && CUSTOM_SECTION_KEY.test(v),
);

/** Any slide of the deck — a built-in or an AE-authored one. */
export const sectionKeySchema = z.union([
  builtInSectionKeySchema,
  customSectionKeySchema,
]);

export type CustomSectionKey = z.infer<typeof customSectionKeySchema>;
export type SectionKey = z.infer<typeof sectionKeySchema>;

export const customSectionKey = (id: string): CustomSectionKey =>
  `custom:${id}`;

/**
 * One slide's hide state (slice 12.5). Carries the explicit desired state
 * rather than flipping whatever is stored, so two rapid clicks or a retried
 * request converge instead of racing.
 */
export const ToggleHiddenSectionDTO = z.object({
  key: sectionKeySchema,
  hidden: z.boolean(),
});

export type ToggleHiddenSection = z.infer<typeof ToggleHiddenSectionDTO>;

export const PACKET_CUSTOM_SECTION_TITLE_MAX = 120;
export const PACKET_CUSTOM_SECTION_RICH_TEXT_MAX = 10_000;

/** Title given to a freshly created slide, before the AE renames it. */
export const NEW_CUSTOM_SECTION_TITLE = 'New slide';

export const packetCustomSectionSchema = z.object({
  /** `randomUUID()`. Also the tail of the slide's comment anchor. */
  id: z.string(),
  title: z.string().min(1).max(PACKET_CUSTOM_SECTION_TITLE_MAX),
  richText: z.string().max(PACKET_CUSTOM_SECTION_RICH_TEXT_MAX),
  /**
   * The built-in section this slide follows; `null` means "before the first
   * built-in". Array order is the order *within* one gap. An `afterKey` naming
   * a built-in that was later retired ranks last — documented, not an error.
   */
  afterKey: builtInSectionKeySchema.nullable(),
  /**
   * Carried forward from the previous packet and not yet looked at. Per-packet,
   * not a durable "this slide is fine" bit: a slide untouched for three packets
   * reads unreviewed each time, which is the point.
   */
  needsReview: z.boolean(),
  updatedAt: z.string(),
});

export type PacketCustomSection = z.infer<typeof packetCustomSectionSchema>;

/**
 * Whole-slide save. Not independent field patches: the card holds two inputs
 * against one element, and per-field patches sharing one `updatedAt` would let
 * a late-arriving body save be silently discarded. The server drops the patch
 * when the stored element's `updatedAt` is strictly newer, exactly as the
 * narrative block merge does.
 */
export const UpdatePacketCustomSectionDTO = z.object({
  title: z.string().trim().min(1).max(PACKET_CUSTOM_SECTION_TITLE_MAX),
  richText: z.string().max(PACKET_CUSTOM_SECTION_RICH_TEXT_MAX),
  updatedAt: z.string(),
});

export type UpdatePacketCustomSection = z.infer<
  typeof UpdatePacketCustomSectionDTO
>;

export const MovePacketCustomSectionDTO = z.object({
  direction: z.enum(['up', 'down']),
});

export type MovePacketCustomSection = z.infer<
  typeof MovePacketCustomSectionDTO
>;

/** `<packetID>:custom:<uuid>` — the first three-segment comment anchor. */
export const customSectionAnchorID = (
  packetID: number,
  slideID: string,
): string => `${packetID}:custom:${slideID}`;
